import React from "react";
import {Field, reduxForm} from "redux-form/immutable";
import {fromJS, List, Map} from "immutable";
import ActionButton from "../../components/ActionButton";
import Checkbox from "../../components/Checkbox";
import FormFieldsTable from "../../components/forms/FormFieldsTable";
import TextInputField from "../../components/forms/TextInputField";
import DropDownInputField from "../../components/forms/DropDownInputField";
import FormError from "../../components/forms/FormError";
import FieldError from "../../components/forms/FieldError";
import {validateFormat, validatePresence} from "../../lib/Validators";
import styles from "./OrderTaggingRuleForm.module.scss";


export const STATUSES = {
  ENABLED: "enabled",
  DISABLED: "disabled",
}

export const ORDER_TYPES = fromJS([
  { id: "new_subscription", name: "New subscription" },
  { id: "recurring_subscription", name: "Recurring subscription" },
]);

const statusOptions = fromJS([
  { value: STATUSES.ENABLED, title: "Enabled" },
  { value: STATUSES.DISABLED, title: "Disabled" },
]);

function splitTags(value) {
  return (value || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);
}

// Tags are edited as a comma separated string (mostly there is only one tag).
export function tagsToString(tags) {
  return List(tags || []).join(", ");
}

// Share validation for both Creation and Edition
export function validateForm(values, props) {
  let errors = Map();
  errors = validatePresence(errors, values, "title");
  errors = validatePresence(errors, values, "accountId");
  errors = validatePresence(errors, values, "skuPattern");
  if (!errors.has("skuPattern")) {
    errors = validateFormat(errors, values.update("skuPattern", (v) => (v || "").trim().toUpperCase()),
      "skuPattern", /^[A-Z]{3}(-[A-Z0-9]+)*$/, "Must be a SKU prefix, e.g. WAT-HAL-STL");
  }

  if (List(values.get("orderTypes") || []).size === 0) {
    errors = errors.set("orderTypes", "Select at least one order type");
  }
  if (splitTags(values.get("tags")).length === 0) {
    errors = errors.set("tags", "Required");
  }

  return errors.toJS();
}

export function prepareValuesForSubmission(values) {
  return Map({
    title: (values.get("title") || "").trim(),
    accountId: (values.get("accountId") || "").trim(),
    orderTypes: List(values.get("orderTypes") || []),
    skuPattern: (values.get("skuPattern") || "").trim().toUpperCase(),
    tags: List(splitTags(values.get("tags"))),
    status: values.get("status"),
  });
}

const OrderTaggingRuleForm = (props) => {
  const {errors, handleSubmit, submitting} = props;
  const submitLabel = submitting ? "Standby..." : "Save";

  return (
    <form onSubmit={handleSubmit}>
      <div className="fields">
        <FormFieldsTable>
          <Field
            label="Title"
            name="title"
            placeholder="Flyer for new subscribers"
            component={TextInputField}
            serverError={errors.title}
            />
          <Field
            label="Account ID"
            name="accountId"
            help="*Must be a US account"
            component={TextInputField}
            serverError={errors.accountId}
            />
          <Field
            label="Order Types"
            name="orderTypes"
            component={OrderTypesField}
            serverError={errors.orderTypes}
            />
          <Field
            label="SKU Pattern"
            name="skuPattern"
            placeholder="WAT-HAL-STL"
            help="*Matches SKUs starting with this"
            component={TextInputField}
            serverError={errors.skuPattern}
            />
          <Field
            label="Tags"
            name="tags"
            placeholder="tag1, tag2"
            help="*Comma separated list of tags"
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            component={TextInputField}
            serverError={errors.tags}
            />
          <Field
            label="Status"
            name="status"
            component={DropDownInputField}
            data={statusOptions}
            showBlank={false}
            serverError={errors.status}
            />
        </FormFieldsTable>
      </div>
      <FormError error={errors._error} />
      <div className="buttons">
        <ActionButton label="Delete" active={!submitting} onClick={props.onDelete} primary={false} show={props.showDelete} />
        <ActionButton label="Cancel" active={!submitting} onClick={props.onCancel} />
        <ActionButton label={submitLabel} onClick={handleSubmit(values => props.onSubmit(values))} active={!submitting} />
      </div>
    </form>
  );
}

// Multi-select of order types, stored as a list of ids.
function OrderTypesField(props) {
  const {input, meta, serverError, label} = props;
  const selected = List(input.value || []);

  function handleClick(id) {
    const updated = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : ORDER_TYPES.map((t) => t.get("id")).filter((x) => x === id || selected.includes(x));
    input.onChange(updated);
  }

  const elements = ORDER_TYPES.map((orderType) => (
    <Checkbox
      key={orderType.get("id")}
      label={orderType.get("name")}
      checked={selected.includes(orderType.get("id"))}
      onClick={() => handleClick(orderType.get("id"))} />
  ));

  return (
    <tr className="field">
      <th>{label}</th>
      <td>
        <div className={styles.orderTypes}>
          {elements}
        </div>
        <FieldError serverError={serverError} clientError={meta.error} showClientError={meta.touched && meta.error} />
      </td>
    </tr>
  );
}

OrderTaggingRuleForm.defaultProps = {
  showDelete: false,
}

const ReduxForm = reduxForm({
  form: "OrderTaggingRuleForm"
})(OrderTaggingRuleForm);

export default ReduxForm;
